import {FC, useState} from 'react'
import {deliveryTypes} from 'app/constant'
import {RootState} from 'setup'
import {
  checkCompliance,
  selectEditOrder,
  selectIsProductsEditMode,
  selectIsShippingAddressSame,
  selectOrderDiscounts,
  selectShippingIsCustom,
  setIsCustomShipping,
  setIsDirty,
  setRemoveShippingDiscount,
  setShippingRate,
  updateEditOrder,
} from 'app/features/orders/redux/order/orderEditSlice'
import {Order} from 'app/features/orders/models/EditOrderState'
import {Discount} from 'app/features/orders/models/OrderState'
import {useDispatch, useSelector} from 'react-redux'
import {SummaryItem} from 'app/features/orders/componentsv1/shared'
import {ShippingRates} from 'app/features/orders/componentsv1/shared/shippingRate'
import deliveryMethodService from 'app/services/DeliveryMethodService/DeliveryMethodService'
import {Button} from '_core'
import CustomShipRateDialog from '../deliveryMethodAction/CustomShipRateDialog'

type Props = {
  onUpdateOrder: () => void
  disabled?: boolean
}

const ShippingMethod: FC<Props> = ({onUpdateOrder, disabled}) => {
  const order = useSelector<RootState, Order>(selectEditOrder)
  const discounts = useSelector<RootState, Discount>(selectOrderDiscounts)
  const isProductMode = useSelector<RootState, boolean>(selectIsProductsEditMode)
  const [shippingRate, _setShippingRate] = useState<any>()
  const [showCustomRate, setShowCustomRate] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const isCustomRate = useSelector<RootState, boolean>(selectShippingIsCustom)
  const isShippingAddressSame = useSelector<RootState, boolean>(selectIsShippingAddressSame)

  const dispatch = useDispatch()

  const updateShippingRate = async () => {
    await dispatch(checkCompliance())
    dispatch(setIsDirty(true))
    dispatch(setIsCustomShipping(false))
  }

  const handleFetchShippingMethods = async () => {
    await onUpdateOrder()
    return await deliveryMethodService.getEditOrderRates(order.orderEditId, isShippingAddressSame)
  }

  const handleShippingMethodChange = async (shippingMtd: any) => {
    _setShippingRate({...shippingMtd, id: order.shippingRateId})
    await updateShippingRate()
  }

  const handleRemoveDiscount = async () => {
    await dispatch(setRemoveShippingDiscount())
    await dispatch(updateEditOrder())
  }

  const handleConfirm = async () => {
    dispatch(setShippingRate({...shippingRate, id: order.shippingRateId}))
    handleCloseModal()
    await updateShippingRate()
  }

  const handleShowDialog = () => {
    setShowCustomRate(true)
  }

  const handleCloseDialog = () => {
    setShowCustomRate(false)
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  if (order.orderDeliveryMethod === deliveryTypes.ship) {
    return (
      <SummaryItem
        title={
          <div className='d-flex align-items-center gap-20'>
            <ShippingRates
              showModal={showModal}
              onSetShowModal={setShowModal}
              shippingPrice={order.shippingPrice}
              onFetch={handleFetchShippingMethods}
              onShippingMethodChange={handleShippingMethodChange}
              selectedRateId={shippingRate?.serviceId ?? order.shippingRate?.serviceId}
              freeShipping={{
                ...discounts,
                freeShippingDiscountCodeId: discounts.shippingDiscountCodeId,
                onRemoveDiscount: handleRemoveDiscount,
              }}
              disabled={disabled}
              title='Edit'
              customRateDialog={
                <CustomShipRateDialog
                  onCloseCustomRate={handleCloseDialog}
                  onClose={handleCloseModal}
                />
              }
              showCustomRate={showCustomRate}
              onCustomRateOpen={handleShowDialog}
              shippingRate={order.shippingRate}
            >
              <Button
                id='btnPay'
                title='Confirm'
                className='btn btn-primary-active w-100'
                onClick={handleConfirm}
                disabled={!shippingRate?.serviceId}
              />
            </ShippingRates>
            <span className='text-dark'>Shipping</span>
          </div>
        }
        value={order.shippingPrice}
        isBetween={isProductMode}
      >
        {!discounts.isFreeShipping && (
          <span className='text-muted'>{getTitle(order, isCustomRate)}</span>
        )}
      </SummaryItem>
    )
  }
  return null
}

export default ShippingMethod

const getTitle = (order: Order, isCustomRate: boolean) => {
  if (order.shippingRate && order.shippingRate.title) {
    return order.shippingRate.title
  } else if (order.shippingRate?.serviceName) {
    return order.shippingRate.serviceName
  } else if (order.shippingPrice > 0 || isCustomRate) {
    return 'Custom Shipping Rate'
  }
}
